import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/date";
import { X, Truck, ArrowRight, CheckCircle } from "@phosphor-icons/react";

const STATUS = {
  scheduled: { t: "Scheduled", dot: "#8A857A", c: "bg-surface-inset text-ink-secondary border-border" },
  in_progress: { t: "In Progress", dot: "#C99A00", c: "bg-[#FBF2CC] text-[#7A5B00] border-[#EAD79A]" },
  delivered: { t: "Completed", dot: "#2E7D32", c: "bg-[#E4F2E4] text-[#256428] border-[#BFE0C0]" },
};
const cardLbl = "text-[10.5px] font-bold uppercase tracking-[0.12em] text-ink-muted mb-1";

function Field({ k, v }) {
  return <div><div className={cardLbl}>{k}</div><div className="text-[15px] font-medium text-ink-secondary leading-tight">{v}</div></div>;
}

export default function TransportDetailModal({ transport, onClose, onChanged }) {
  const { isWriter } = useAuth();
  const [t, setT] = useState(transport);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const st = STATUS[t.status] || STATUS.scheduled;
  const assets = (t.asset_nos && t.asset_nos.length) ? t.asset_nos : (t.asset_no ? [t.asset_no] : []);

  const setStatus = async (status) => {
    setBusy(true); setError("");
    try {
      const { data } = await api.put(`/transportation/${t.id}`, { status });
      setT((p) => ({ ...p, ...(data && data.id ? data : { status }) }));
      onChanged?.();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not update status."); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[rgba(26,26,26,0.42)] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface-card border border-border-warm rounded-[18px] overflow-hidden shadow-modal w-full max-w-[560px] max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="transport-detail-modal">
        <div className="px-6 pt-5 pb-4 border-b border-divider-inner shrink-0 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted">Transport</div>
            <h3 className="text-[22px] font-bold text-ink-primary leading-tight mt-0.5 truncate">{assets.join(", ") || "Movement"}</h3>
            <p className="text-[13.5px] text-ink-secondary mt-1 flex items-center gap-1.5 flex-wrap">
              <span className="truncate">{t.from_location || "—"}</span>
              <ArrowRight size={13} weight="bold" className="text-ink-tertiary shrink-0" />
              <span className="truncate font-semibold text-ink-primary">{t.to_location || "—"}</span>
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span data-testid="transport-status" className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.06em] rounded-full border ${st.c}`}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.dot }} /> {st.t}
            </span>
            <button onClick={onClose} data-testid="transport-detail-close" className="w-9 h-9 flex items-center justify-center rounded-[9px] border border-border-control text-ink-secondary hover:bg-surface-panel"><X size={16} weight="bold" /></button>
          </div>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto bg-surface-inset">
          {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-[8px]" data-testid="transport-error">{error}</div>}
          <section className="bg-surface-card border border-border rounded-[12px] p-4 grid grid-cols-2 gap-x-6 gap-y-4">
            <Field k="Assets" v={assets.join(", ") || "—"} />
            <Field k="Scheduled" v={t.scheduled_at ? fmtDateTime(t.scheduled_at) : "—"} />
            <Field k="From" v={t.from_location || "—"} />
            <Field k="To" v={t.to_location || "—"} />
            <Field k="Transported by" v={t.carrier || "Not assigned yet"} />
            <Field k="Status" v={st.t} />
            {t.notes ? <div className="col-span-2"><Field k="Notes" v={t.notes} /></div> : null}
          </section>
        </div>

        {isWriter && t.status !== "delivered" && (
          <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-border bg-surface-panel shrink-0">
            <p className="text-[12.5px] text-ink-tertiary max-w-[50%] leading-snug">Update this movement as it progresses. Completing it updates each asset's location.</p>
            <div className="flex items-center gap-3">
              {t.status === "scheduled" && (
                <button onClick={() => setStatus("in_progress")} disabled={busy} data-testid="transport-mark-in-progress"
                  className="h-11 px-5 border border-border-control bg-white text-ink-primary text-[12px] uppercase tracking-[0.05em] font-bold rounded-[10px] hover:bg-surface-panel">Mark In Progress</button>
              )}
              <button onClick={() => setStatus("delivered")} disabled={busy} data-testid="transport-mark-completed"
                className="h-11 px-6 bg-equipr-yellow text-black text-[12px] uppercase tracking-[0.05em] font-bold rounded-[10px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px inline-flex items-center gap-1.5"><Truck size={15} weight="bold" /> Mark Completed</button>
            </div>
          </div>
        )}
        {t.status === "delivered" && (
          <div className="flex items-center justify-end px-6 py-4 border-t border-border bg-surface-panel shrink-0">
            <span className="text-[13px] font-semibold text-green-700 inline-flex items-center gap-1"><CheckCircle size={16} weight="fill" /> Movement completed</span>
          </div>
        )}
      </div>
    </div>
  );
}
